<template>
  <div>

    <!-- Labeled dropdown buttons; never wraps (the shared toolbar sheds other elements first). -->
    <div class="dv-filters-separate flex flex-nowrap items-center gap-2 flex-shrink-0">
      <FilterDropdown v-if="showTeachers && allTeachers.length"
        label="Teachers" :options="teacherOptions" v-model:open="teacherDropdownOpen"
        @toggle="$emit('toggle-teacher', $event)" @clear="$emit('deselect-all')" />
      <FilterDropdown v-if="allInstruments.length"
        label="Instrument" :options="instrumentOptions"
        @toggle="$emit('toggle-instrument', $event)" @clear="clearInstruments" />
      <FilterDropdown v-if="allLocations.length"
        label="Location" :options="locationOptions"
        @toggle="$emit('select-location', $event)" @clear="clearLocation" />
    </div>

    <!-- Very narrow toolbars: icon-only buttons with active-count badges. Toggled by the dv-toolbar container query below. -->
    <div class="dv-filters-icons hidden items-center gap-2 flex-shrink-0">
      <FilterIconDropdown v-if="showTeachers && allTeachers.length"
        :icon="UserGroupIcon" label="Teachers" :badge-count="activeTeachers.size" v-model:open="teacherDropdownOpen">
        <FilterOptionList :options="teacherOptions" @toggle="$emit('toggle-teacher', $event)" @clear="$emit('deselect-all')" />
      </FilterIconDropdown>
      <FilterIconDropdown v-if="allInstruments.length"
        :icon="MusicalNoteIcon" label="Instrument" :badge-count="allInstruments.filter(isInstrumentActive).length">
        <FilterOptionList :options="instrumentOptions" @toggle="$emit('toggle-instrument', $event)" @clear="clearInstruments" />
      </FilterIconDropdown>
      <FilterIconDropdown v-if="allLocations.length"
        :icon="MapPinIcon" label="Location" :badge-count="selectedLocation !== null ? 1 : 0">
        <FilterOptionList :options="locationOptions" @toggle="$emit('select-location', $event)" @clear="clearLocation" />
      </FilterIconDropdown>
    </div>

  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, onActivated, onDeactivated } from 'vue'
import { UserGroupIcon, MusicalNoteIcon, MapPinIcon } from '@heroicons/vue/24/outline'
import { avatarGradient } from '@/composables/useAvatarGradient.js'
import { useActiveTeacherDropdown } from '@/composables/useActiveTeacherDropdown.js'
import FilterDropdown from './FilterDropdown.vue'
import FilterOptionList from './FilterOptionList.vue'
import FilterIconDropdown from './FilterIconDropdown.vue'

const props = defineProps({
  showTeachers:        { type: Boolean, default: true },
  allTeachers:         { type: Array,   default: () => [] },
  allInstruments:      { type: Array,   default: () => [] },
  allLocations:        { type: Array,   default: () => [] },
  activeTeachers:      { type: Object,  required: true },  // Set
  selectedLocation:    { type: String,  default: null },
  isDark:              { type: Boolean, default: false },
  isInstrumentActive:  { type: Function, required: true },
  isInstrumentPartial: { type: Function, required: true },
})

const emit = defineEmits(['toggle-teacher', 'deselect-all', 'toggle-instrument', 'select-location'])

const teacherColor = name => avatarGradient(name)

// ── Narrow-screen dropdown option lists ──────────────────────────────────────
const teacherOptions = computed(() =>
  props.allTeachers.map(t => ({ value: t, label: t, active: props.activeTeachers.has(t), dot: teacherColor(t) }))
)
const instrumentOptions = computed(() =>
  props.allInstruments.map(inst => ({ value: inst, label: inst, active: props.isInstrumentActive(inst) }))
)
const locationOptions = computed(() =>
  props.allLocations.map(loc => ({ value: loc, label: loc === '__none__' ? 'None' : loc, active: props.selectedLocation === loc }))
)

const clearInstruments = () => {
  props.allInstruments.filter(props.isInstrumentActive).forEach(inst => emit('toggle-instrument', inst))
}
const clearLocation = () => {
  if (props.selectedLocation !== null) emit('select-location', props.selectedLocation)
}

// "t" (see AppRoot.vue's global shortcuts) opens the Teachers dropdown from
// anywhere on a calendar view (Day/Week/Month, admin or dashboard), same
// registration pattern as useActiveSearchFocus/TableToolbar's "s" shortcut.
const teacherDropdownOpen = ref(false)
const activeTeacherDropdown = useActiveTeacherDropdown()
const openTeacherDropdown = () => { teacherDropdownOpen.value = true }
const teacherDropdownAvailable = () => props.showTeachers && props.allTeachers.length > 0
// "c" clears every filter at once (teachers, instruments, location).
const clearAllFilters = () => {
  if (props.activeTeachers.size) emit('deselect-all')
  clearInstruments()
  clearLocation()
}
// Activated/Deactivated: see TableToolbar.vue's "s" registration — same keep-alive gap.
onMounted(() => activeTeacherDropdown.register(openTeacherDropdown, teacherDropdownAvailable, clearAllFilters))
onActivated(() => activeTeacherDropdown.register(openTeacherDropdown, teacherDropdownAvailable, clearAllFilters))
onDeactivated(() => activeTeacherDropdown.unregister(openTeacherDropdown))
onUnmounted(() => activeTeacherDropdown.unregister(openTeacherDropdown))
</script>

<style scoped>
/* Swaps the 3 labeled dropdowns for icon-only ones when the toolbar (DayView's dv-toolbar container) gets tight; leaves headroom for the trailing "..." actions button. */
@container dv-toolbar (max-width: 520px) {
  .dv-filters-separate { display: none !important; }
  .dv-filters-icons { display: flex !important; }
}
</style>
